import { Component } from '@theme/component';

const TINT_URL_PARAM = 'tint';

const TINT_URL_EVENTS = {
  confirmed: 'color_selection_confirmed',
  cleared: 'color_selection_cleared',
};

const TINT_URL_SELECTORS = {
  picker: 'tint-color-picker',
  colorCard: '.color-browse-grid__card',
  selectedPickerButton: '.tint-color-picker__button.is-selected',
};

/**
 * @param {HTMLElement} card
 */
function getColorDetailFromCard(card) {
  return {
    colorRef: card.getAttribute('data-color-ref') || '',
    colorName: card.getAttribute('data-color-name') || '',
    colorHex: card.getAttribute('data-color-hex') || '',
    colorId: card.getAttribute('data-color-id') || '',
    colorBase: card.getAttribute('data-color-base') || '',
    colorHandle: card.getAttribute('data-color-handle') || '',
    colorCollections: card.getAttribute('data-color-collections') || '',
    colorFilters: card.getAttribute('data-color-filters') || '',
    colorImage: card.getAttribute('data-color-image') || '',
    sourceElement: card,
  };
}

/**
 * @returns {string}
 */
function readTintFromUrl() {
  return new URL(window.location.href).searchParams.get(TINT_URL_PARAM)?.trim() || '';
}

/**
 * @param {string} handle
 */
function writeTintToUrl(handle) {
  const url = new URL(window.location.href);
  const normalized = handle.trim();

  if (normalized) {
    url.searchParams.set(TINT_URL_PARAM, normalized);
  } else {
    url.searchParams.delete(TINT_URL_PARAM);
  }

  const next = url.toString();
  if (next !== window.location.href) {
    history.replaceState({}, '', next);
  }
}

/**
 * Keeps ?tint={handle} in sync with the PDP color picker.
 * Variant ID updates are handled by variant-picker.js and keep existing URL params.
 */
class TintUrlSync extends Component {
  /** @type {HTMLElement | null} */
  #picker = null;

  connectedCallback() {
    super.connectedCallback();

    this.#picker = this.closest(TINT_URL_SELECTORS.picker);
    if (!this.#picker) return;

    this.#picker.addEventListener(TINT_URL_EVENTS.confirmed, this.#onColorConfirmed);
    this.#picker.addEventListener(TINT_URL_EVENTS.cleared, this.#onColorCleared);

    requestAnimationFrame(() => {
      if (this.#picker) this.#restoreFromUrl(this.#picker);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.#picker) return;

    this.#picker.removeEventListener(TINT_URL_EVENTS.confirmed, this.#onColorConfirmed);
    this.#picker.removeEventListener(TINT_URL_EVENTS.cleared, this.#onColorCleared);
  }

  /** @param {CustomEvent} event */
  #onColorConfirmed = (event) => {
    const handle = event.detail?.colorHandle;
    if (handle) writeTintToUrl(handle);
  };

  #onColorCleared = () => {
    writeTintToUrl('');
  };

  /**
   * @param {HTMLElement} picker
   */
  #restoreFromUrl(picker) {
    const handle = readTintFromUrl();
    if (!handle) return;

    if (picker.querySelector(TINT_URL_SELECTORS.selectedPickerButton)) return;

    const card = picker.querySelector(
      `${TINT_URL_SELECTORS.colorCard}[data-color-handle="${CSS.escape(handle)}"]`,
    );

    if (!(card instanceof HTMLElement)) return;

    const cards = picker.querySelectorAll(TINT_URL_SELECTORS.colorCard);
    for (const button of cards) {
      const el = /** @type {HTMLElement} */ (button);
      el.setAttribute('aria-checked', el === card ? 'true' : 'false');
    }

    picker.dispatchEvent(
      new CustomEvent(TINT_URL_EVENTS.confirmed, {
        bubbles: true,
        detail: getColorDetailFromCard(card),
      }),
    );
  }
}

customElements.define('tint-url-sync', TintUrlSync);
